<template>
 <site-shell auth-page>
  <div class="auth-page bs-foundation">
   <div class="auth-column">
    <h1 class="auth-heading">{{ heading }}</h1>
    <p v-if="!confirmed" class="auth-lede">{{ register ? 'Track every fermentation. Free on the Taster plan.' : 'Check gravity, temperature and alerts for every brew.' }}</p>

    <div class="auth-card bs:bg-surface bs:rounded-sm bs:shadow-card">
     <div v-if="confirmed" class="auth-confirmed">
      <div class="auth-notice" :class="confirmationOk ? 'auth-notice-success' : 'auth-notice-error'" role="status">
       <p>{{ confirmationOk ? 'Your account is confirmed. Your brews are ready when you are.' : 'This confirmation link is invalid or has expired.' }}</p>
      </div>
      <a v-if="confirmationOk" class="bs-button bs-button--primary auth-wide" href="/dashboard">Go to your dashboard</a>
      <a class="bs-button auth-wide" href="/login">Log in</a>
     </div>

     <template v-else>
      <form class="auth-form" novalidate @submit.prevent="submit">
       <p v-if="loading" class="auth-status" role="status">Loading security token…</p>
       <div v-if="loadError" class="auth-notice auth-notice-error" role="alert">
        <p>Unable to prepare the form. <button type="button" class="bs-button" @click="load">Try again</button></p>
       </div>
       <div v-if="summary.length" ref="summary" id="auth-error" class="auth-notice auth-notice-error" role="alert" tabindex="-1">
        <p v-for="(line, index) in summary" :key="'summary-' + index">{{ line }}</p>
        <p v-if="credentialFailure">Try again, or <a href="/account/resetpwd">reset your password</a>.</p>
       </div>

       <div v-for="field in fields" :key="field.name" class="bs-field">
        <div class="auth-label-row">
         <label :for="field.name">{{ field.label }}</label>
         <a v-if="field.name === 'Password' && !register" href="/account/resetpwd">Forgot password?</a>
        </div>
        <div :class="field.name === 'Password' ? 'auth-password' : 'auth-input'">
         <input class="bs-input" :id="field.name" v-model="draft[field.name]"
          :type="field.name === 'Password' && showPassword ? 'text' : field.type"
          :autocomplete="field.autocomplete" :minlength="field.minlength" :maxlength="field.maxlength"
          :autocapitalize="field.type === 'password' ? null : 'none'" :spellcheck="field.type === 'password' ? null : 'false'"
          required :aria-invalid="invalid(field.name) ? 'true' : 'false'" :aria-describedby="describedBy(field)">
         <button v-if="field.name === 'Password'" type="button" class="bs-button auth-reveal"
          aria-controls="Password" aria-label="Show password" :aria-pressed="showPassword ? 'true' : 'false'"
          @click="showPassword = !showPassword">{{ showPassword ? 'Hide' : 'Show' }}</button>
        </div>
        <p v-if="field.hint" :id="field.name + '-hint'" class="auth-hint">{{ field.hint }}</p>
        <p v-for="(error, index) in inlineErrors(field.name)" :key="field.name + '-error-' + index"
         :id="index === 0 ? field.name + '-error' : null" class="auth-field-error">{{ error }}</p>
       </div>

       <div v-if="!register" class="bs-field">
        <label class="auth-remember" for="RememberMe"><input id="RememberMe" type="checkbox" v-model="draft.RememberMe"> Keep me signed in</label>
       </div>
       <div v-if="register && recaptchaEnabled" ref="captcha" class="auth-captcha"></div>
       <p v-if="register && recaptchaEnabled && widget === null && !captchaError && !loading && !loadError" class="auth-status" role="status">Loading captcha…</p>
       <div v-if="captchaError" class="auth-notice auth-notice-error" role="alert"><p>{{ captchaError }} <AppButton @click="loadCaptcha">Try again</AppButton></p></div>

       <AppButton type="submit" variant="primary" class="auth-wide" :loading="form.pending" :disabled="loading || loadError || (register && recaptchaEnabled && widget === null)">{{ form.pending ? (register ? 'Creating account…' : 'Logging in…') : register ? 'Create account' : 'Log in' }}</AppButton>
       <p v-if="register" class="auth-terms">By signing up you agree to the <a href="/terms">Terms of Use</a> and <a href="/privacy-policy">Privacy Policy</a>.</p>
      </form>

      <div class="auth-divider">or</div>
      <div id="socialLoginList">
       <a class="bs-button auth-wide auth-provider auth-google" href="/external-login?provider=google"><IconBrandGoogle class="bs-icon" :size="20" :stroke="2" aria-hidden="true" focusable="false" />{{ register ? 'Sign up' : 'Log in' }} with Google</a>
       <a class="bs-button auth-wide auth-provider auth-facebook" href="/external-login?provider=facebook"><IconBrandFacebook class="bs-icon" :size="20" :stroke="2" aria-hidden="true" focusable="false" />{{ register ? 'Sign up' : 'Log in' }} with Facebook</a>
      </div>
      <p class="auth-switch">
       <template v-if="register">Already have an account? <a href="/login">Log in</a></template>
       <template v-else>New to Brew Status? <a href="/register">Create a free account</a></template>
      </p>
     </template>
    </div>

   </div>
  </div>
 </site-shell>
</template>
<script>
import axios from 'axios';
import createAuth from './authForm';
import SiteShell from './SiteShell.vue';
import AppButton from '../components/ui/AppButton.vue';
import { IconBrandGoogle, IconBrandFacebook } from '@tabler/icons-vue';
const NETWORK_FAILURE = 'Unable to save. Please try again.';
const FIELD_SUMMARY = 'Please correct the fields below.';
export default {
 components: { SiteShell, AppButton, IconBrandGoogle, IconBrandFacebook },
 data() {
  return {
   register: /^\/register\/?$/i.test(location.pathname),
   confirmed: /^\/accountconfirmed\/?$/i.test(location.pathname),
   draft: { Email: '', Username: '', Password: '', ConfirmPassword: '', RememberMe: false },
   token: '', recaptchaEnabled: true, loading: true, loadError: false, widget: null, captchaError: '', showPassword: false, localErrors: {}, disposed: false,
   form: createAuth(async draft => {
    const body = new URLSearchParams();
    Object.keys(draft).forEach(k => body.append(k, draft[k]));
    body.append('__RequestVerificationToken', this.token);
    body.append('g-recaptcha-response', this.register && window.grecaptcha && this.widget !== null ? window.grecaptcha.getResponse(this.widget) : '');
    return axios.post('/application/auth/' + (this.register ? 'register' : 'login') + location.search, body);
   }, url => location.assign(url))
  };
 },
 computed: {
  heading() { return this.register ? 'Sign up for Brew Status' : this.confirmed ? 'Account confirmation' : 'Log in to Brew Status'; },
  confirmationOk() { return new URLSearchParams(location.search).get('status') === 'confirmed'; },
  fields() {
   if (this.register) return [
    { name: 'Username', label: 'Username', type: 'text', autocomplete: 'username', minlength: 3, maxlength: 50, hint: '3 to 50 letters, numbers, dots, dashes or underscores. You can log in with it or your email.' },
    { name: 'Email', label: 'Email address', type: 'email', autocomplete: 'email' },
    { name: 'Password', label: 'Password', type: 'password', autocomplete: 'new-password', minlength: 6, maxlength: 100, hint: 'At least 6 characters.' }
   ];
   return [
    { name: 'Email', label: 'Username or email', type: 'text', autocomplete: 'username' },
    { name: 'Password', label: 'Password', type: 'password', autocomplete: 'current-password' }
   ];
  },
  fieldNames() { return this.fields.map(f => f.name); },
  // Login failures are about the pair of credentials, never one field, so they become one message.
  credentialFailure() { return !this.register && !Object.keys(this.localErrors).length && this.fieldNames.some(name => (this.form.errors[name] || []).length); },
  summary() {
   const lines = [];
   if (this.credentialFailure) lines.push("That username or email and password don't match.");
   Object.keys(this.form.errors).forEach(key => {
    if (this.fieldNames.indexOf(key) >= 0 || key === 'ConfirmPassword') return;
    this.form.errors[key].forEach(e => lines.push(key === 'GRecaptchaResponse' ? 'Confirm you are not a robot, then try again.' : e));
   });
   const message = this.form.message;
   if (message === NETWORK_FAILURE) lines.push("Couldn't reach Brew Status. Check your connection and try again.");
   else if (message && message !== FIELD_SUMMARY) lines.push(message);
   return lines;
  }
 },
 methods: {
  inlineErrors(name) {
   if (this.localErrors[name]) return [this.localErrors[name]];
   if (!this.register) return [];
   const errors = (this.form.errors[name] || []).slice();
   // ConfirmPassword is sent automatically; surface its errors on the one password field.
   if (name === 'Password') (this.form.errors.ConfirmPassword || []).forEach(e => errors.push(e));
   return errors;
  },
  invalid(name) { return this.credentialFailure || this.inlineErrors(name).length > 0; },
  describedBy(field) {
   const ids = [];
   if (field.hint) ids.push(field.name + '-hint');
   if (this.inlineErrors(field.name).length) ids.push(field.name + '-error');
   if (this.credentialFailure) ids.push('auth-error');
   return ids.length ? ids.join(' ') : null;
  },
  async load() {
   this.loading = true; this.loadError = false;
   try {
    const settings = (await axios.get('/application/auth/token')).data;
    if (this.disposed) return;
    this.token = settings.token; this.recaptchaEnabled = settings.recaptchaEnabled !== false;
    if (this.register && this.recaptchaEnabled) { await this.$nextTick(); this.loadCaptcha(); }
   } catch (e) { this.loadError = true; } finally { this.loading = false; }
  },
  async submit() {
   const missing = {};
   this.fields.forEach(f => { if (!String(this.draft[f.name] || '').trim()) missing[f.name] = 'Enter your ' + f.label.toLowerCase() + '.'; });
   this.localErrors = missing;
   const firstMissing = this.fieldNames.filter(name => missing[name])[0];
   if (firstMissing) { this.form.errors = {}; this.form.message = ''; document.getElementById(firstMissing).focus(); return; }
   this.draft.ConfirmPassword = this.draft.Password;
   await this.form.submit(this.draft);
   if (this.register && window.grecaptcha && this.widget !== null) window.grecaptcha.reset(this.widget);
   await this.$nextTick();
   if (this.summary.length && this.$refs.summary) this.$refs.summary.focus();
   else {
    const first = this.fieldNames.filter(name => this.inlineErrors(name).length)[0];
    if (first) document.getElementById(first).focus();
   }
  },
  loadCaptcha() {
   this.captchaError = '';
   if (this.captchaScript) this.captchaScript.remove();
   const render = () => {
    if (this.disposed || !this.$refs.captcha) return;
    try {
     this.widget = window.grecaptcha.render(this.$refs.captcha, { sitekey: '6LcL7t0qAAAAAMcpSLpYLkVnN1ToySsyhKB5Zrqd', size: this.$refs.captcha.clientWidth < 304 ? 'compact' : 'normal' });
    } catch (_) { this.captchaError = 'Unable to load captcha. Reload and try again.'; }
   };
   if (window.grecaptcha && window.grecaptcha.render) render();
   else {
    window.brewStatusCaptchaReady = render;
    this.captchaReady = render;
    const script = document.createElement('script');
    this.captchaScript = script;
    script.src = 'https://www.google.com/recaptcha/api.js?onload=brewStatusCaptchaReady&render=explicit';
    script.onerror = () => { if (!this.disposed) this.captchaError = 'Unable to load captcha. Reload and try again.'; };
    script.async = true; document.head.appendChild(script);
   }
  }
 },
 beforeUnmount() {
  this.disposed = true;
  if (this.captchaScript) this.captchaScript.remove();
  if (window.brewStatusCaptchaReady === this.captchaReady) delete window.brewStatusCaptchaReady;
  if (window.grecaptcha && this.widget !== null) window.grecaptcha.reset(this.widget);
 },
 created() {
  document.title = (this.register ? 'Sign up' : this.confirmed ? 'Account confirmation' : 'Log in') + ' · Brew Status';
  if (!this.confirmed) this.load();
 }
};
</script>
